import {useAuth} from 'react-oidc-context'
import {useGetUsersQuery} from '../../auth/AuthApiSlice'
import {CorrespondenceMessageType, CorrespondenceParticipantRole} from '../interfaces'
import {
  ICorrespondenceHost,
  ICorrespondenceThreadDefaults,
  IRecipientProviderResult,
  IRecipientResult,
} from './types'
import {IUserDetails} from '../../settings/interfaces/IUserDetails'
import {IFinancierAnalystAssignment} from '../../financiers/interfaces/IFinancierAnalystAssignment'

function mapRoleToParticipantRole(role?: string): CorrespondenceParticipantRole {
  if (!role) return CorrespondenceParticipantRole.Admin
  const r = role.toLowerCase()
  if (r === 'financier') return CorrespondenceParticipantRole.Financier
  if (r === 'grant provider') return CorrespondenceParticipantRole.GrantProvider
  if (r.includes('analyst')) return CorrespondenceParticipantRole.Analyst
  if (r.includes('manager')) return CorrespondenceParticipantRole.Manager
  return CorrespondenceParticipantRole.Admin
}

/**
 * Financier users contact internal staff (analysts, managers, admins).
 * Other financier accounts are excluded from the recipient pool.
 */
function useFinancierRecipients(searchQuery: string): IRecipientProviderResult {
  const auth = useAuth()
  const {data, isLoading} = useGetUsersQuery(
    {page: 1, pageSize: 200},
    {skip: !auth.isAuthenticated}
  )

  const all: IUserDetails[] = data
    ? (Object.values(data.entities).filter(Boolean) as IUserDetails[])
    : []

  // Financier users can only message internal staff — exclude other financier accounts
  const internalStaff = all.filter((u) => !u.role?.toLowerCase().includes('financier'))

  const q = searchQuery.toLowerCase().trim()
  const filtered = q
    ? internalStaff.filter(
        (u) =>
          `${u.firstName} ${u.lastName}`.toLowerCase().includes(q) ||
          u.email?.toLowerCase().includes(q)
      )
    : internalStaff

  return {
    recipients: filtered.map(
      (u): IRecipientResult => ({
        id: u.id,
        displayName: `${u.firstName} ${u.lastName}`.trim(),
        email: u.email,
        participantRole: mapRoleToParticipantRole(u.role),
      })
    ),
    isLoading,
  }
}

/**
 * Factory — call at the FinancierRoutes boundary where financierId is known.
 */
export function createFinancierHost(
  financierId: string,
  primaryAnalyst?: IFinancierAnalystAssignment | null,
  isLoadingAnalyst?: boolean
): ICorrespondenceHost {
  const defaults: ICorrespondenceThreadDefaults = {financierId}

  const lockedRecipients: IRecipientResult[] | undefined = primaryAnalyst
    ? [
        {
          id: primaryAnalyst.analystUserId,
          displayName: primaryAnalyst.analystName,
          email: primaryAnalyst.analystEmail,
          participantRole: CorrespondenceParticipantRole.Analyst,
        },
      ]
    : undefined

  return {
    inboxRoute: () => `/financier/${financierId}/correspondence`,
    threadRoute: (id) => `/financier/${financierId}/correspondence/${id}`,
    useRecipients: useFinancierRecipients,
    getThreadDefaults: () => defaults,
    canStartThread: !isLoadingAnalyst,
    canReply: true,
    hideRecipientPicker: true,
    defaultMessageType: CorrespondenceMessageType.External,
    lockedRecipients,
  }
}
